import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { Navigate, useLocation } from "@tanstack/react-router";
import { useEffect, useState, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../design-system/Button";
import { AccessState } from "../../design-system/AccessState";
import { useAuthenticatedOwner } from "../../app/useAuthenticatedOwner";
import { backendApi } from "../../backend/api";
import { TitlePreferenceContext } from "../catalog/titlePreference";
import { FormattingLocaleContext } from "../../i18n/formattingLocaleContext";
import { getSuggestedProfileDefaults } from "./defaults";
import type { ViewerProfile } from "../../backend/api";
import { cacheViewerProfile, readCachedViewerProfile } from "../../offline/profileSnapshot";

export function ProfileGate({ children }: PropsWithChildren) {
  const { t } = useTranslation("account");
  const { isAuthenticated, isLoading } = useConvexAuth();
  const pathname = useLocation({ select: (location) => location.pathname });
  const profile = useQuery(backendApi.profiles.viewer, isAuthenticated ? {} : "skip");
  const bootstrapProfile = useMutation(backendApi.profiles.bootstrap);
  const [bootstrapError, setBootstrapError] = useState(false);
  const [cachedProfile, setCachedProfile] = useState<ViewerProfile | null | undefined>(undefined);
  // Who the token says is signed in. Known before any query answers, which is
  // what lets the cache stand in during an outage rather than only offline.
  const ownerId = useAuthenticatedOwner();

  useEffect(() => {
    let active = true;
    void readCachedViewerProfile(ownerId).then((value) => {
      if (active) setCachedProfile(value);
    });
    return () => {
      active = false;
    };
  }, [ownerId]);

  useEffect(() => {
    if (profile === undefined || profile === null) return;
    void cacheViewerProfile(profile);
  }, [profile]);

  useEffect(() => {
    if (!isAuthenticated || profile !== null) {
      return;
    }
    let active = true;
    void bootstrapProfile(getSuggestedProfileDefaults()).catch(() => {
      if (active) {
        setBootstrapError(true);
      }
    });
    return () => {
      active = false;
    };
  }, [bootstrapProfile, isAuthenticated, profile]);

  /*
   * The cached profile stands in for a query that has not answered -- but only
   * one belonging to the account holding the token.
   *
   * A session that expires or is revoked without `secureSignOut` leaves the
   * previous account's snapshot on the device, and handing that to whoever
   * signs in next is the failure this guards. `readCachedViewerProfile` is now
   * asked for `ownerId`'s snapshot specifically, so a stale partition misses
   * rather than answering.
   *
   * That is also why this no longer waits on `navigator.onLine`. The browser's
   * online bit says an interface is up, not that Convex is reachable: during a
   * backend outage, a captive portal, or a blocked WebSocket, `profile` stays
   * `undefined` forever while `isOnline` stays true -- and withholding the
   * cache on that basis left every protected route stuck on a loading state
   * with a perfectly good offline copy on disk.
   */
  // `null` is an answer -- this account has no profile yet, and bootstrap runs.
  // Only an unresolved query falls through to the cache.
  const availableProfile = profile === undefined ? cachedProfile : profile;

  if (isLoading || (isAuthenticated && profile === undefined && cachedProfile === undefined)) {
    return (
      <AccessState
        description={t("auth.loadingDescription")}
        state="loading"
        title={t("auth.loading")}
      />
    );
  }

  if (!isAuthenticated) {
    return children;
  }

  if (availableProfile === undefined) {
    return (
      <AccessState
        description={t("auth.loadingDescription")}
        state="loading"
        title={t("auth.loading")}
      />
    );
  }

  if (bootstrapError) {
    return (
      <AccessState
        action={
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            {t("common.tryAgain")}
          </Button>
        }
        description={t("profile.bootstrapErrorDescription")}
        state="error"
        title={t("profile.bootstrapErrorTitle")}
      />
    );
  }

  if (availableProfile === null) {
    return (
      <AccessState
        description={t("profile.preparingDescription")}
        state="loading"
        title={t("profile.preparing")}
      />
    );
  }

  if (!availableProfile.onboardingCompleted && pathname !== "/onboarding") {
    return <Navigate to="/onboarding" />;
  }

  if (availableProfile.onboardingCompleted && pathname === "/onboarding") {
    return <Navigate to="/" />;
  }

  // Everything below this gate has a profile, so this is the one place that can
  // publish the saved format to every formatter without each surface querying
  // the profile itself.
  return (
    <FormattingLocaleContext value={availableProfile.formattingLocale}>
      <TitlePreferenceContext value={availableProfile.titleDisplayPreference}>
        {children}
      </TitlePreferenceContext>
    </FormattingLocaleContext>
  );
}
